"use client";

import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Button, Link } from "ui-components";

import { initializeBraze } from "@/utils/brazeClient";
import { getLocalStorage, setLocalStorage } from "@/utils/localStorage";

export default function CookieBanner(): JSX.Element {
  const pathname = usePathname();
  const [cookieConsent, setCookieConsent] = useState<string | boolean>(true);

  const isWidget = pathname.startsWith("/widget");
  const isJobPage = pathname.startsWith("/jobs/");
  const isHidden = cookieConsent === true;

  useEffect(() => {
    const storedCookieConsent = getLocalStorage("cookie_consent", null);
    setCookieConsent(storedCookieConsent);
  }, []);

  useEffect(() => {
    setLocalStorage("cookie_consent", cookieConsent);
  }, [cookieConsent]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    const utmSource = url.searchParams.get("utm_source");
    const isOrganisationPage = pathname.startsWith("/organizations");

    const shouldInit =
      (!utmSource || utmSource !== "trafmic") && !isOrganisationPage;

    if (shouldInit) {
      initializeBraze();
    }
  }, [pathname]);

  const handleAccept = (): void => {
    setCookieConsent(true);
  };

  const cookieText = useMemo(
    () => (
      <>
        This website uses cookies to ensure you get the best experience.{" "}
        <Link href="/cookies-policy" className="text-white underline">
          Learn more
        </Link>
      </>
    ),
    []
  );

  if (isWidget || isHidden) {
    return <></>;
  }

  // Job page - show at top on mobile, bottom on desktop
  if (isJobPage) {
    return (
      <>
        {/* Mobile - Top */}
        <div className="flex md:hidden fixed top-0 inset-x-0 z-[100] justify-center bg-[#5f6c7b] p-4">
          <div className="flex flex-col gap-3 items-center w-full">
            <p className="text-sm text-white text-center">{cookieText}</p>
            <Button
              onClick={handleAccept}
              type="button"
              className="w-full max-w-[240px]"
            >
              Got it!
            </Button>
          </div>
        </div>

        {/* Desktop - Bottom */}
        <div className="hidden md:flex fixed inset-x-0 bottom-0 z-20 justify-center gap-x-8 gap-y-4 bg-[#5d6a77] p-6 ring-1 ring-[#5d6a77] md:flex-row md:items-center lg:px-8">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-20 flex justify-between items-center w-full flex-col sm:flex-row gap-3">
            <p className="text-sm text-white">{cookieText}</p>
            <div className="flex flex-none items-center">
              <Button onClick={handleAccept} type="button">
                Got it!
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // All other pages - bottom for all screen sizes
  return (
    <div className="flex flex-col fixed inset-x-0 bottom-0 z-20 justify-center gap-x-8 gap-y-4 bg-[#5d6a77] p-6 ring-1 ring-[#5d6a77] md:flex-row md:items-center lg:px-8">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-20 flex justify-between items-center w-full flex-col sm:flex-row gap-3">
        <p className="text-sm text-white">{cookieText}</p>
        <div className="flex flex-none items-center">
          <Button onClick={handleAccept} type="button">
            Got it!
          </Button>
        </div>
      </div>
    </div>
  );
}
